import {UserAgent} from "@/model/user_agent";
import {message} from "ant-design-vue";
import dayjs from "dayjs";
import {api} from "@/lib/api";
import {computed} from "vue";

export interface IBranch {
    branch_id: string;
    brand_id: string;
    name: string;
    province: string;
    city: string;
    district: string;
    address: string;
    coordinate: number[];
    tel: string;
    wx_account_qr_code: string | null;
    subscribe_wx_open_id: string | null;
    trash_at: string | null;
}

export interface IBrandInfo {
    title_style: number;
    content: string;
    images: string[];
}

export interface IBrand extends IBrandInfo {
    brand_id: string;
    agent_id: string;
    icon: string | null;
    name: string;
    branches: IBranch[];
    create_at: string;
    trash_at: string | null;
}

export class BrandManager {

    brand = computed(() => this.userAgent.brand(this.userAgent.active.account.active_brand_id ?? ''))

    constructor(private readonly userAgent: UserAgent) {
    }

    updateBrandIcon(images: string[]) {
        const d = {brand_id: this.userAgent.active.account.active_brand_id, icon: null as string | null, name: ''};
        if (images.length > 0) {
            d.icon = images[0];
        }
        this.userAgent.active.brands.forEach(brand => {
            if (brand.brand_id == this.userAgent.active.account.active_brand_id) d.name = brand.name;
        })
        const done = message.loading('保存中', 0)
        api({url: `user/brand/${this.userAgent.active.account.active_brand_id}`, method: 'PUT', data: d}).then(() => {
            if (this.brand.value) this.brand.value.icon = d.icon;
        }).finally(done);
    }

    updateBrandName(name: string): Promise<void> {
        if (name.length < 2) return Promise.reject('字数太短');
        const d = {brand_id: this.userAgent.active.account.active_brand_id, icon: null as string | null, name: name};
        this.userAgent.active.brands.forEach(brand => {
            if (brand.brand_id == this.userAgent.active.account.active_brand_id) d.icon = brand.icon;
        })
        const done = message.loading('保存中', 0)
        return api({
            url: `user/brand/${this.userAgent.active.account.active_brand_id}`,
            method: 'PUT',
            data: d
        }).then(() => {
            if (this.brand.value) this.brand.value.name = d.name;
        }).finally(done);
    }

    async updateInfo(brand_id: string): Promise<unknown> {
        const done = message.loading('更新中', 0)
        return api({
            url: `user/brand/${this.userAgent.active.account.active_brand_id}/info`,
            method: 'PUT',
            data: this.userAgent.brand(brand_id)
        }).finally(done)
    }

    remove(brand_id: string): Promise<void> {
        if (brand_id == this.userAgent.active.account.active_brand_id) {
            message.error('此品牌已被选中');
            return Promise.reject('此品牌已被选中')
        }
        const done = message.loading('删除中', 0)
        return api({url: `user/brand/${brand_id}`, method: 'DELETE'}).then(() => {
            if (this.brand.value) this.brand.value.trash_at = dayjs().format('YYYY-MM-DD HH:mm:ss');
        }).finally(done);
    }

    restore(brand_id: string): Promise<void> {
        const done = message.loading('恢复中', 0)
        return api({url: `user/brand/${brand_id}`, method: 'PATCH'}).then(() => {
            if (this.brand.value) this.brand.value.trash_at = null
        }).finally(done);
    }

    available(): IBrand[] {
        return this.userAgent.active.brands.filter(b => b.trash_at)
    }

    async newBranch(b: IBranch): Promise<IBranch> {
        return await api<void, IBranch>({
            url: `user/brand/${this.userAgent.active.account.active_brand_id}/branch`,
            method: 'POST',
            data: b
        }).then(res => {
            this.userAgent.active.brands.forEach(brand => {
                if (brand.brand_id == res.brand_id) {
                    brand.branches.unshift(res)
                }
            })
            return res;
        })
    }

    updateBranch(br: IBranch): Promise<void> {
        return api({
            url: `user/brand/${br.brand_id}/branch/${br.branch_id}`,
            method: 'PUT',
            data: br
        }).then(() => {
            this.userAgent.active.brands.forEach((brand, x) => {
                if (brand.brand_id == br.brand_id) brand.branches.forEach((branch, y) => {
                    if (branch.branch_id == br.branch_id) this.userAgent.active.brands[x].branches[y] = br
                })
            })
        })
    }

    removeBranch(branch_id: string): Promise<void> {
        const done = message.loading('移除中', 0)
        return api({
            url: `user/brand/${this.userAgent.active.account.active_brand_id}/branch/${branch_id}`,
            method: 'DELETE',
        }).then(() => {
            this.userAgent.active.brands.forEach(brand => {
                if (brand.brand_id == this.userAgent.active.account.active_brand_id) brand.branches.forEach(branch => {
                    if (branch.branch_id == branch_id) branch.trash_at = dayjs().format('YYYY-MM-DD HH:mm:ss');
                })
            })
        }).finally(done)
    }

    restoreBranch(branch_id: string): Promise<void> {
        const done = message.loading('恢复中', 0)
        return api({
            url: `user/brand/${this.userAgent.active.account.active_brand_id}/branch/${branch_id}`,
            method: 'PATCH'
        }).then(() => {
            this.userAgent.active.brands.forEach(brand => {
                if (brand.brand_id == this.userAgent.active.account.active_brand_id) brand.branches.forEach(branch => {
                    if (branch.branch_id == branch_id) branch.trash_at = null
                })
            })
        }).finally(done)
    }

    getBrandNames(branch_ids: string[]): { [key: string]: string } {
        const bs: { [key: string]: string } = {}
        this.userAgent.active.brands.forEach(brand => {
            brand.branches.forEach(branch => {
                if (branch_ids.includes(branch.branch_id)) bs[brand.brand_id] = brand.name
            })
        })
        return bs
    }

    getPreviewNameByBranchId(branch_id: string): string | null {
        let name: string | null = null
        this.userAgent.active.brands.forEach(brand => {
            brand.branches.forEach(branch => {
                if (branch.branch_id == branch_id) name = `${brand.name}-${branch.name}`
            })
        })
        return name
    }
}
